/**
 * Quick License Plate Calculator - Types
 *
 * Strongly-typed interfaces for calculator state and results.
 * These types are specific to the calculator feature and do not
 * affect any other part of the application.
 */

import type { RdwDraft } from '@/lib/rdwClient';

/**
 * Calculator error types mapped from RDW and calculation failures
 */
export type CalculatorError =
  | 'INVALID_PLATE'
  | 'NOT_FOUND'
  | 'RATE_LIMITED'
  | 'RDW_UNAVAILABLE'
  | 'MISSING_DATA';

/**
 * Source of the gross BPM value
 */
export type GrossBpmSource = 'RDW' | 'CALCULATED' | 'UNKNOWN';

/**
 * BPM calculation result
 */
export interface BpmResult {
  /** Gross BPM (bruto BPM) in euros, null if unavailable */
  grossBpm: number | null;
  /** Source of the gross BPM value */
  grossBpmSource: GrossBpmSource;
  /** Rest BPM (te betalen BPM) in euros, null if unavailable */
  restBpm: number | null;
  /** Depreciation percentage (0-100), null if unavailable */
  depreciationPct: number | null;
  /** Months since first registration, null if unavailable */
  monthsSinceFirstUse: number | null;
  /** Forfaitaire band label (e.g., "18 - <30 maanden"), null if unavailable */
  bandLabel: string | null;
  /** Whether the 5-year rule was applied (BESTELAUTO only) */
  cappedBy5YearRule?: boolean;
}

/**
 * VAT/Margin classification result
 */
export type VatMarginClassification = 'LIKELY_MARGIN' | 'LIKELY_VAT' | 'UNCERTAIN';

/**
 * Classification confidence level
 */
export type ClassificationConfidence = 'LOW' | 'MEDIUM' | 'HIGH';

/**
 * Full classification result with reasoning
 */
export interface ClassificationResult {
  /** The classification outcome */
  classification: VatMarginClassification;
  /** Confidence level of the classification */
  confidence: ClassificationConfidence;
  /** Human-readable reasons for the classification (Dutch) */
  reasons: string[];
}

/**
 * Risk flags from RDW data analysis
 */
export interface RiskFlags {
  isImport: boolean;
  missingCo2: boolean;
  missingBpm: boolean;
  missingFirstRegDate: boolean;
  isElectric: boolean;
}

/**
 * Existing vehicle lookup result
 * - null: no vehicle found (or not yet looked up)
 * - 'UNKNOWN': lookup failed (Firestore error)
 * - object: vehicle found with id and displayName
 */
export type ExistingVehicleResult = { id: string; displayName: string } | null | 'UNKNOWN';

/**
 * Complete calculator state
 */
export interface CalculatorState {
  /** Whether the calculator popover is open */
  isOpen: boolean;
  /** Current license plate input */
  plate: string;
  /** RDW data from lookup, null if not fetched */
  rdwData: RdwDraft | null;
  /** BPM calculation result, null if not calculated */
  bpm: BpmResult | null;
  /** VAT/Margin classification, null if not classified */
  classification: ClassificationResult | null;
  /** Risk flags, null if not analyzed */
  riskFlags: RiskFlags | null;
  /** Max buy price in euros, null if not calculated */
  maxBuyPrice: number | null;
  /** User input: expected selling price in euros */
  expectedSellingPrice: number | null;
  /** User input: target margin percentage (default 10) */
  targetMarginPct: number;
  /** Loading state */
  isLoading: boolean;
  /** Current error, null if no error */
  error: CalculatorError | null;
  /** Error message for display */
  errorMessage: string | null;
  /** Existing vehicle lookup result (non-blocking) */
  existingVehicle: ExistingVehicleResult;
}

/**
 * Initial calculator state
 */
export const INITIAL_CALCULATOR_STATE: CalculatorState = {
  isOpen: false,
  plate: '',
  rdwData: null,
  bpm: null,
  classification: null,
  riskFlags: null,
  maxBuyPrice: null,
  expectedSellingPrice: null,
  targetMarginPct: 10,
  isLoading: false,
  error: null,
  errorMessage: null,
  existingVehicle: null,
};

/**
 * Calculator hook return type
 */
export interface UseQuickCalculatorReturn {
  /** Current calculator state */
  state: CalculatorState;
  /** Retry countdown in seconds (0 = no countdown active) */
  retryCountdown: number;
  /** Open the calculator popover */
  open: () => void;
  /** Close the calculator popover */
  close: () => void;
  /** Set the license plate input */
  setPlate: (plate: string) => void;
  /** Set the expected selling price */
  setExpectedSellingPrice: (price: number | null) => void;
  /** Set the target margin percentage */
  setTargetMarginPct: (pct: number) => void;
  /** Trigger RDW lookup and calculations */
  calculate: () => Promise<void>;
  /** Reset calculator to initial state */
  reset: () => void;
  /** Set existing vehicle lookup result (called by popover after lookup) */
  setExistingVehicle: (result: ExistingVehicleResult) => void;
}

/**
 * Prefill payload for creating vehicles/invoices from calculator results.
 * This is a small, stable payload that can be encoded in URL query params.
 */
export interface CalculatorPrefill {
  /** Payload version for forward compatibility */
  v: 1;
  /** Normalized license plate */
  plate: string;
  /** Vehicle make/brand */
  make: string | null;
  /** Vehicle model */
  model: string | null;
  /** Vehicle class (e.g., "PERSONENAUTO") */
  vehicleClass: string | null;
  /** Fuel type */
  fuelType: string | null;
  /** First registration date (ISO string) */
  firstRegDate: string | null;
  /** First registration date in NL (ISO string) */
  firstRegDateNL: string | null;
  /** CO₂ emissions in g/km */
  co2Gkm: number | null;
  /** Gross BPM in euros */
  grossBpm: number | null;
  /** Rest BPM in euros (calculated) */
  restBpm: number | null;
  /** Whether the vehicle is an import */
  isImport: boolean;
  /** Source indicator */
  source: 'calculator';
}
